"use client";

import { useMemo } from "react";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import { useMonarchRule } from "@/monarch/rules/MonarchRulesProvider";
import {
  TRENDING_ON_EDX_DEFAULT_CATEGORY_KEY_ORDER,
  type Category,
  type TrendingOnEdxProductTabsData,
  type TrendingOnEdxProductTabsResult,
} from "@/components/ProductCardListWrapper/variants/ProductCardTabsAndPillsByUUID/types";

const ALLOWED_CATEGORY_KEYS = new Set<Category["key"]>([
  "course",
  "executiveEducation",
  "certificate",
  "bachelors",
  "masters",
]);

/** Drops empty strings and unknown keys from Monarch arrays. */
function sanitizeCategoryKeyOrder(
  order: Category["key"][] | undefined,
): Category["key"][] | undefined {
  if (!order?.length) return undefined;
  const filtered = order.filter(
    (k) =>
      typeof k === "string" &&
      k.trim().length > 0 &&
      ALLOWED_CATEGORY_KEYS.has(k as Category["key"]),
  ) as Category["key"][];
  return filtered.length > 0 ? filtered : undefined;
}

type HomepageTrendingCourseUpdateMonarchValue = {
  /** When `false`, rule UI matches baseline even if feature flag is `test`. Omit or `true` to apply `data`. */
  enable?: boolean;
  data?: TrendingOnEdxProductTabsData;
};

/**
 * Cohesion Monarch rule id — pair with feature flag
 * `NEXT_PUBLIC_FEATURE_HOMEPAGE_TRENDING_COURSE_UPDATE` (flag `test` enables this rule’s data).
 */
const RULE_ID = "homepageTrendingCourseUpdate";

const LEGACY_DATA: TrendingOnEdxProductTabsData = {
  prioritizeCoursesFirst: false,
  categoryKeyOrder: undefined,
  showTrendingFlameIcon: false,
};

const TEST_DEFAULTS: TrendingOnEdxProductTabsData = {
  prioritizeCoursesFirst: true,
  categoryKeyOrder: TRENDING_ON_EDX_DEFAULT_CATEGORY_KEY_ORDER,
  showTrendingFlameIcon: true,
};

function mergeTestData(
  raw?: TrendingOnEdxProductTabsData,
): TrendingOnEdxProductTabsData {
  if (!raw) return { ...TEST_DEFAULTS };

  const prioritizeCoursesFirst =
    raw.prioritizeCoursesFirst ?? TEST_DEFAULTS.prioritizeCoursesFirst;

  let categoryKeyOrder: TrendingOnEdxProductTabsData["categoryKeyOrder"];
  if (raw.categoryKeyOrder !== undefined) {
    categoryKeyOrder = sanitizeCategoryKeyOrder(raw.categoryKeyOrder);
  } else if (prioritizeCoursesFirst) {
    categoryKeyOrder = TEST_DEFAULTS.categoryKeyOrder;
  } else {
    categoryKeyOrder = undefined;
  }

  return {
    prioritizeCoursesFirst,
    categoryKeyOrder,
    showTrendingFlameIcon:
      raw.showTrendingFlameIcon ?? TEST_DEFAULTS.showTrendingFlameIcon,
  };
}

export default function useHomepageTrendingCourseUpdate(): TrendingOnEdxProductTabsResult {
  const { flagValue, isLoading: flagLoading } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_HOMEPAGE_TRENDING_COURSE_UPDATE",
    "legacy",
  );

  const monarchEnabled =
    !flagLoading && flagValue !== null && flagValue === "test";

  const { isLoading: monarchLoading, value } =
    useMonarchRule<HomepageTrendingCourseUpdateMonarchValue>(
      RULE_ID,
      undefined,
      {
        enabled: monarchEnabled,
      },
    );

  return useMemo((): TrendingOnEdxProductTabsResult => {
    if (flagLoading || flagValue === null) {
      return {
        isLoading: true,
        variant: "legacy",
        data: LEGACY_DATA,
      };
    }

    if (flagValue !== "test") {
      return {
        isLoading: false,
        variant: "legacy",
        data: LEGACY_DATA,
      };
    }

    if (monarchLoading) {
      return {
        isLoading: true,
        variant: "test",
        data: TEST_DEFAULTS,
      };
    }

    if (value?.enable === false) {
      return {
        isLoading: false,
        variant: "test",
        data: LEGACY_DATA,
      };
    }

    return {
      isLoading: false,
      variant: "test",
      data: mergeTestData(value?.data),
    };
  }, [flagLoading, flagValue, monarchLoading, value]);
}
